// src/services/VehicleInfoService.js
import API_CONFIG from "./ApiConfig";

/**
 * Service for handling vehicle information API calls
 */
const VehicleInfoService = {
  /**
   * Fetch all available vehicle makes
   * 
   * @param {string} token - Firebase authentication token
   * @returns {Promise<Array>} - Array of vehicle makes with id and name properties
   */
  fetchVehicleMakes: async (token) => {
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/vehicle/info/edmunds/makes`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-user-token": token,
          },
        }
      );
      
      if (!response.ok) {
        throw new Error("Failed to fetch vehicle makes");
      }
      
      const makes = await response.json();
      
      // Ensure makes is an array and has the expected structure
      const makesArray = Array.isArray(makes) ? makes : (makes?.data || []);
      
      return makesArray;
    } catch (error) {
      console.error("Error fetching vehicle makes:", error);
      throw error;
    }
  },

  /**
   * Fetch vehicle models for a specific make
   * 
   * @param {string} makeName - Name of the vehicle make
   * @param {string} token - Firebase authentication token
   * @returns {Promise<Array>} - Array of vehicle models with id and name properties
   */
  fetchVehicleModels: async (makeName, token) => {
    try {
      const makeNameLower = makeName.toString().toLowerCase();
      
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/vehicle/info/edmunds/models?make=${encodeURIComponent(makeNameLower)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-user-token": token,
          },
        }
      );
      
      if (!response.ok) {
        throw new Error("Failed to fetch vehicle models");
      }
      
      const models = await response.json();
      
      // Ensure models is an array and has the expected structure
      const modelsArray = Array.isArray(models) ? models : (models?.data || []);
      
      return modelsArray;
    } catch (error) {
      console.error("Error fetching vehicle models:", error);
      throw error;
    }
  },

  /**
   * Generate an array of years for vehicle selection
   * 
   * @param {number} yearsBack - How many years back from current year (default: 30)
   * @returns {Array<number>} - Array of years in descending order
   */
  generateYearOptions: (yearsBack = 30) => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i < yearsBack; i++) {
      years.push(currentYear - i);
    }
    return years;
  },

  /**
   * Validate VIN format
   * 
   * @param {string} vin - VIN to validate
   * @returns {boolean} - True if VIN format is valid
   */
  validateVIN: (vin) => {
    if (!vin || typeof vin !== 'string') return false;
    return vin.length === 17 && /^[A-HJ-NPR-Z0-9]{17}$/i.test(vin);
  },

  /**
   * Validate mileage input
   * 
   * @param {string|number} mileage - Mileage to validate
   * @returns {boolean} - True if mileage is valid
   */
  validateMileage: (mileage) => {
    const mileageNum = parseInt(mileage);
    return !isNaN(mileageNum) && mileageNum >= 0 && mileageNum <= 999999;
  }
};

export default VehicleInfoService;